import { useState, useMemo } from 'react'
import {
  Navbar,
  Block,
  BlockTitle,
  List,
  ListItem,
  Searchbar,
} from 'konsta/react'
import {
  UserX,
  CheckCircle2,
  Settings,
  DoorClosed,
  Scale,
  Unlock,
  Wrench,
  UserPlus,
  Clock,
  ClipboardList,
  Search,
} from 'lucide-react'
import { formatRelativeTime } from '@/lib/utils'

// --- Types ---

type AuditAction =
  | 'user_ban'
  | 'withdrawal_approve'
  | 'config_change'
  | 'room_close'
  | 'dispute_resolve'
  | 'frozen_pot_release'
  | 'maintenance_mode'
  | 'agent_approve'

interface AuditLog {
  id: string
  action: AuditAction
  admin: string
  target: string
  description: string
  date: Date
}

// --- Mock Data ---

const mockAuditLogs: AuditLog[] = [
  { id: '1', action: 'user_ban', admin: 'Admin', target: 'Alex Kim', description: '停權用戶 - 疑似使用外掛', date: new Date(Date.now() - 1000 * 60 * 30) },
  { id: '2', action: 'withdrawal_approve', admin: 'Admin', target: 'W003', description: '批准提款 $200.00 - Sarah Lin', date: new Date(Date.now() - 1000 * 60 * 60) },
  { id: '3', action: 'config_change', admin: 'Admin', target: 'rake_rate', description: '修改抽水比例 5% → 4.5%', date: new Date(Date.now() - 1000 * 60 * 60 * 3) },
  { id: '4', action: 'room_close', admin: 'Admin', target: 'PLO 新手桌', description: '關閉房間 - 維護', date: new Date(Date.now() - 1000 * 60 * 60 * 8) },
  { id: '5', action: 'dispute_resolve', admin: 'Admin', target: 'D003', description: '解決爭議 - 未發現異常', date: new Date(Date.now() - 1000 * 60 * 60 * 12) },
  { id: '6', action: 'frozen_pot_release', admin: 'Admin', target: 'FP003', description: '釋放凍結底池 $120.00', date: new Date(Date.now() - 1000 * 60 * 60 * 24) },
  { id: '7', action: 'maintenance_mode', admin: 'Admin', target: 'system', description: '啟用維護模式 - 系統更新', date: new Date(Date.now() - 1000 * 60 * 60 * 48) },
  { id: '8', action: 'agent_approve', admin: 'Admin', target: 'Tony Zhang', description: '批准代理申請', date: new Date(Date.now() - 1000 * 60 * 60 * 72) },
]

// --- Helpers ---

function getActionConfig(action: AuditAction) {
  switch (action) {
    case 'user_ban':
      return { label: '用戶停權', Icon: UserX, color: '#FF3B30' }
    case 'withdrawal_approve':
      return { label: '提款批准', Icon: CheckCircle2, color: '#34C759' }
    case 'config_change':
      return { label: '設定變更', Icon: Settings, color: '#FF9500' }
    case 'room_close':
      return { label: '關閉房間', Icon: DoorClosed, color: '#8E8E93' }
    case 'dispute_resolve':
      return { label: '爭議解決', Icon: Scale, color: '#007AFF' }
    case 'frozen_pot_release':
      return { label: '釋放底池', Icon: Unlock, color: '#AF52DE' }
    case 'maintenance_mode':
      return { label: '維護模式', Icon: Wrench, color: '#FF9500' }
    case 'agent_approve':
      return { label: '代理批准', Icon: UserPlus, color: '#34C759' }
  }
}

function groupLogsByDate(logs: AuditLog[]): { label: string; logs: AuditLog[] }[] {
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000)

  const today: AuditLog[] = []
  const yesterday: AuditLog[] = []
  const earlier: AuditLog[] = []

  logs.forEach((log) => {
    if (log.date >= todayStart) {
      today.push(log)
    } else if (log.date >= yesterdayStart) {
      yesterday.push(log)
    } else {
      earlier.push(log)
    }
  })

  const groups: { label: string; logs: AuditLog[] }[] = []
  if (today.length > 0) groups.push({ label: '今天', logs: today })
  if (yesterday.length > 0) groups.push({ label: '昨天', logs: yesterday })
  if (earlier.length > 0) groups.push({ label: '更早', logs: earlier })

  return groups
}

// --- Component ---

export function AdminAuditScreen() {
  const [searchQuery, setSearchQuery] = useState('')

  const filteredLogs = useMemo(() => {
    if (!searchQuery.trim()) return mockAuditLogs
    const q = searchQuery.toLowerCase()
    return mockAuditLogs.filter(
      (log) =>
        log.description.toLowerCase().includes(q) ||
        log.target.toLowerCase().includes(q) ||
        log.admin.toLowerCase().includes(q) ||
        getActionConfig(log.action).label.includes(q),
    )
  }, [searchQuery])

  const groupedLogs = useMemo(() => groupLogsByDate(filteredLogs), [filteredLogs])

  return (
    <>
      <Navbar title="審計日誌" />

      {/* Search */}
      <Searchbar
        value={searchQuery}
        onInput={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
        onClear={() => setSearchQuery('')}
        placeholder="搜尋審計記錄..."
        disableButton
      />

      {/* Summary */}
      <Block className="!px-4 !mt-2 !mb-0">
        <div className="rounded-2xl bg-white p-4 shadow-sm border border-black/5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#007AFF]/15 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-[#007AFF]" />
          </div>
          <div>
            <p className="font-semibold text-black">
              {filteredLogs.length} 筆審計記錄
            </p>
            <p className="text-[12px] text-[#8E8E93]">
              {searchQuery ? `搜尋「${searchQuery}」的結果` : '顯示所有記錄'}
            </p>
          </div>
        </div>
      </Block>

      {/* Grouped Audit Logs */}
      {groupedLogs.length > 0 ? (
        groupedLogs.map((group) => (
          <div key={group.label}>
            <BlockTitle>{group.label}</BlockTitle>
            <List strongIos insetIos>
              {group.logs.map((log) => {
                const config = getActionConfig(log.action)
                const Icon = config.Icon
                return (
                  <ListItem
                    key={log.id}
                    media={
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${config.color}15` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: config.color }} />
                      </div>
                    }
                    title={
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{config.label}</span>
                        <span
                          className="text-[11px] px-1.5 py-0.5 rounded bg-[#F2F2F7] text-[#8E8E93]"
                        >
                          {log.target}
                        </span>
                      </div>
                    }
                    subtitle={
                      <div>
                        <p className="text-[#8E8E93] text-[12px] leading-tight mt-0.5">
                          {log.description}
                        </p>
                        <span className="flex items-center gap-1 text-[#C7C7CC] text-[11px] mt-1">
                          <Clock className="w-3 h-3" />
                          {formatRelativeTime(log.date)} &middot; {log.admin}
                        </span>
                      </div>
                    }
                  />
                )
              })}
            </List>
          </div>
        ))
      ) : (
        <Block className="text-center py-12">
          <Search className="w-12 h-12 mx-auto text-[#C7C7CC] mb-3" />
          <p className="text-[#8E8E93] text-[15px]">沒有找到匹配的審計記錄</p>
        </Block>
      )}

      {/* Bottom padding */}
      <div className="h-8" />
    </>
  )
}
